"use client";

import { useEffect, useRef } from "react";
import {
  type ActiveChip,
  type FilterControllerProps,
  type FilterMenuItem,
  SingleSelectFilterMenu,
  useNamespacedTranslations,
  useRegisterFilter,
  useRouterParams,
} from "@zapier/shared";
import {
  useConnectionActionPermissions,
  useSplitTreatments,
} from "@zapier/client";

// "Owned by me" lives in the owner domain (handled by the quick filter that
// writes `?owner=`), not here. Everything below is access-state.
type SharingValue =
  | "-me"
  | "shared-by-me"
  | "managed"
  | "everyone"
  | "private"
  | "inactive-members";

const CHIP_KEY_BY_VALUE: Record<SharingValue, string> = {
  "-me": "sharedWithMe",
  "shared-by-me": "sharedByMe",
  managed: "managed",
  everyone: "everyone",
  private: "private",
  "inactive-members": "inactiveMembers",
};

const ICON = "personGroup";

// Owns `?shared=`; also writes `ownerStatus=inactive` atomically when the user
// picks "Inactive members". Several options are permission/flag-gated.
export function ConnectionSharingFilterController({
  isOpen,
  namespace,
  onClose,
  onInteraction,
}: FilterControllerProps): JSX.Element | null {
  const t = useNamespacedTranslations(namespace);
  const { shared, setParams } = useRouterParams();
  const { canFilterByPrivateAndSharedWithEveryone, canFilterByOwnerStatus } =
    useConnectionActionPermissions({});
  const { isManagedConnectionsFilterEnabled } = useSplitTreatments();

  // Defer close until the URL reflects the new value.
  const closeOnNextValue = useRef(false);
  useEffect(() => {
    if (closeOnNextValue.current && shared) {
      closeOnNextValue.current = false;
      onClose();
    }
  }, [shared, onClose]);

  const value: SharingValue | undefined =
    shared && shared !== "all" && shared in CHIP_KEY_BY_VALUE
      ? (shared as SharingValue)
      : undefined;
  const hasValue = !!value;
  const chipKey = value ? CHIP_KEY_BY_VALUE[value] : undefined;
  const label = chipKey ? t(`sharing.chipLabel.${chipKey}`) : "";
  const ariaLabel = chipKey ? t(`sharing.chipAriaLabel.${chipKey}`) : "";

  let chip: ActiveChip | undefined;
  let signature = "sharing:none";
  if (hasValue) {
    chip = {
      key: "sharing",
      iconName: ICON,
      title: t("sharing.label"),
      label,
      ariaLabel,
    };
    signature = `sharing:${value}`;
  } else if (isOpen) {
    chip = {
      key: "sharing",
      iconName: ICON,
      title: t("sharing.label"),
      ariaLabel: t("sharing.label"),
      isDraft: true,
    };
    signature = "sharing:draft";
  }

  const adminOnlyLabel = t("sharing.options.adminOnly");
  const items: FilterMenuItem[] = [
    { id: "-me", name: t("sharing.options.sharedWithMe") },
    { id: "shared-by-me", name: t("sharing.options.sharedByMe") },
    ...(isManagedConnectionsFilterEnabled
      ? [{ id: "managed", name: t("sharing.options.managed") }]
      : []),
    ...(canFilterByPrivateAndSharedWithEveryone
      ? [
          {
            id: "everyone",
            name: t("sharing.options.everyone"),
            description: adminOnlyLabel,
          },
          {
            id: "private",
            name: t("sharing.options.private"),
            description: adminOnlyLabel,
          },
        ]
      : []),
    ...(canFilterByOwnerStatus
      ? [
          {
            id: "inactive-members",
            name: t("sharing.options.inactiveMembers"),
            description: adminOnlyLabel,
          },
        ]
      : []),
  ];

  const applySharing = (next: SharingValue) => {
    if (next === value) {
      onClose();
      return;
    }
    closeOnNextValue.current = true;
    // "inactive-members" pairs with ownerStatus=inactive; everything else
    // clears it. The owner-domain `?owner=` is untouched here.
    if (next === "inactive-members") {
      setParams({ shared: next, ownerStatus: "inactive", page: 1 });
    } else {
      setParams({ shared: next, page: 1 }, ["ownerStatus"]);
    }
    onInteraction?.();
  };

  useRegisterFilter({
    type: "sharing",
    hasValue,
    signature,
    chip,
    renderMenu: () => (
      <SingleSelectFilterMenu
        label={t("sharing.label")}
        items={items}
        selectedId={value}
        onSelect={(id) => {
          if (id in CHIP_KEY_BY_VALUE) {
            applySharing(id as SharingValue);
          } else {
            onClose();
          }
        }}
      />
    ),
    // React Aria submenu (Filters-button path): RA closes the tree on select.
    // `applySharing` no-ops when the picked value matches the current one.
    menuOptions: {
      label: t("sharing.label"),
      items,
      selectedId: value,
      onSelect: (id) => {
        if (id in CHIP_KEY_BY_VALUE) applySharing(id as SharingValue);
      },
    },
  });
  // eslint-disable-next-line unicorn/no-null -- headless controller renders nothing
  return null;
}
